// Polyfills for ES2022 runtime APIs that dependencies call unconditionally and
// that crash older browsers during boot on the login page:
//   Object.hasOwn      — es-toolkit via @inertiajs/vue3 useForm (Sentry SPECTRE-ID-84)
//   Array.prototype.at — @zag-js via @ark-ui/vue / spectre-ui   (Sentry SPECTRE-ID-CM)
// Vite/esbuild transpiles syntax but does not polyfill runtime APIs, so these
// must be imported first — before any dependency code runs.
import 'core-js/actual/object/has-own';
import 'core-js/actual/array/at';

import './bootstrap';
import '../css/app.css';

import type { DefineComponent } from 'vue';
import { createApp, h } from 'vue';
import { createInertiaApp } from '@inertiajs/vue3';
import { resolvePageComponent } from 'laravel-vite-plugin/inertia-helpers';
import * as Sentry from '@sentry/vue';
import { createPinia } from 'pinia';
import { ZiggyVue } from 'ziggy-js';
import { configureEcho } from '@laravel/echo-vue';
import AppLayout from '@/Layouts/AppLayout.vue';

declare global {
  interface Window {
    // Set true once the SPA mounts; read by the inline boot-watchdog in app.blade.php.
    __bootOK?: boolean;
  }
}

const appName = import.meta.env.VITE_APP_NAME || 'Spectre Accounts Service';
const pinia = createPinia();

createInertiaApp({
  title: (title) => `${title} - ${appName}`,
  resolve: async (name) => {
    const page = await resolvePageComponent(`./Pages/${name}.vue`, import.meta.glob<DefineComponent>('./Pages/**/*.vue'));
    page.default.layout = page.default.layout || AppLayout;
    return page;
  },
  setup({
    el, App, props, plugin,
  }) {
    const broadcasting = props.initialPage.props.broadcasting as { key: string; cluster: string };

    configureEcho({
      broadcaster: 'pusher',
      key: broadcasting.key,
      cluster: broadcasting.cluster,
      forceTLS: true,
    });

    const app = createApp({ render: () => h(App, props) });

    Sentry.init({
      app,
      dsn: import.meta.env.VITE_SENTRY_DSN_PUBLIC,
      environment: import.meta.env.MODE,
    });

    app
      .use(plugin)
      .use(ZiggyVue)
      .use(pinia)
      .mount(el);

    // Signal the inline boot-watchdog (app.blade.php) that boot succeeded.
    window.__bootOK = true;
  },
  progress: {
    color: '#4B5563',
  },
});
